import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  Router,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { TraceService } from '@sentry/angular-ivy';
import { CookieService } from 'ngx-cookie-service';
import { LottieModule } from 'ngx-lottie';
import { AuthInterceptor } from './app/auth/interceptors/auth-interceptor';
import { ErrorInterceptor } from './app/auth/interceptors/error-interceptor';
import { ErrorRetryInterceptor } from './app/auth/interceptors/error-retry-interceptor';
import { ChromeExtensionRoutes } from './app/chrome-extension-routes';
import { playerFactory } from './app/configs/player-factory';
import { CustomErrorHandlerService } from './app/error-handler';
import { SentryTransactionInterceptor } from './app/interceptors/sentry-transaction-interceptor';
import { AppConfigService } from './app/providers/config.service';
import { routes } from './app/routes';
import { AnalyticsService } from './app/services/analytics.service';
import { BrowserService } from './app/services/browser.service';
import { MetaService } from './app/services/meta.service';
import { ScrollService } from './app/services/scroll.service';
import { tenantRoutes } from './app/tenant-routes';
import { environment } from './environments/environment';

function initializeApp(
  scroll: ScrollService,
  an: AnalyticsService,
  meta: MetaService,
  config: AppConfigService,
  browserService: BrowserService,
  router: Router
) {
  return () => {
    const routes = getRoutes(browserService);
    if (routes) router.resetConfig(routes);
    console.log('ANGULAR_INIT_APP');
  };
}

function getRoutes(browserService: BrowserService) {
  const isTenantDomain = browserService.isTenantDomain();
  if (browserService.isExtension()) {
    return ChromeExtensionRoutes;
  } else {
    if (isTenantDomain) return tenantRoutes;
  }
}

export const appConfig = {
  providers: [
    importProvidersFrom(LottieModule.forRoot({ player: playerFactory })),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    ...(environment.extension ? [] : [provideClientHydration()]),
    provideAnimations(),
    CookieService,
    { provide: APP_ID, useValue: 'picflow' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SentryTransactionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorRetryInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ScrollService, AnalyticsService, MetaService, AppConfigService, BrowserService, Router],
      multi: true,
    },
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    {
      provide: TraceService,
      deps: [Router],
    },
  ],
};
